import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getNodeByKey, $isElementNode, $nodesOfType } from 'lexical';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  $isCollapsibleContainerNode,
  CollapsibleContainerNode,
} from '../nodes/CollapsibleContainerNode';

export function $removeCollapsibleContainer(container) {
  const next = container.getNextSibling();
  const previous = container.getPreviousSibling();

  if ($isElementNode(next)) {
    container.remove();
    next.selectStart();
    return;
  }

  if ($isElementNode(previous)) {
    container.remove();
    previous.selectEnd();
    return;
  }

  const paragraph = $createParagraphNode();
  container.replace(paragraph);
  paragraph.select();
}

function $measureContainers(editor, anchorElem) {
  const anchorRect = anchorElem.getBoundingClientRect();
  const positions = [];

  for (const node of $nodesOfType(CollapsibleContainerNode)) {
    const dom = editor.getElementByKey(node.getKey());
    if (!dom) continue;
    const rect = dom.getBoundingClientRect();
    const titleRect = (dom.querySelector('summary') || dom).getBoundingClientRect();
    positions.push({
      key: node.getKey(),
      top: titleRect.top + titleRect.height / 2 - anchorRect.top,
      right: Math.round(anchorRect.right - rect.right),
    });
  }

  return positions;
}

export default function CollapsibleDeletePlugin({ anchorElem }) {
  const [editor] = useLexicalComposerContext();
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    const measure = () => {
      const next = editor.getEditorState().read(() => $measureContainers(editor, anchorElem));
      setPositions(previous =>
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
      );
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(anchorElem);
    window.addEventListener('resize', measure);
    const unregisterUpdateListener = editor.registerUpdateListener(measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      unregisterUpdateListener();
    };
  }, [editor, anchorElem]);

  const remove = key => {
    editor.update(() => {
      const container = $getNodeByKey(key);
      if ($isCollapsibleContainerNode(container)) {
        $removeCollapsibleContainer(container);
      }
    });
    editor.focus();
  };

  return createPortal(
    positions.map(({ key, top, right }) => (
      <button
        key={key}
        type="button"
        aria-label="Delete accordion"
        title="Delete accordion"
        className="absolute z-[1] flex size-6 items-center justify-center rounded-md text-black dark:text-foreground opacity-50 hover:bg-gray-100 dark:hover:bg-muted hover:opacity-100"
        style={{ top: top - 12, right: right + 4 }}
        onMouseDown={event => event.preventDefault()}
        onClick={() => remove(key)}
      >
        <X className="size-3" strokeWidth={2.4} />
      </button>
    )),
    anchorElem,
  );
}
